import { iconImageId, loadLayerIcons } from './hybridActivityIcons';

// One clustered GeoJSON source per configured layer, so each layer clusters
// on its own: Mapbox clusters everything in a source together, and a cluster
// mixing two layers could not be told apart on the map. The ids carry the
// match value, which toLayerConfig has already made unique.
export const pointsSourceId = (matchValue) =>
    `hybrid-activity-points:${matchValue}`;
export const circleLayerId = (matchValue) =>
    `hybrid-activity-points-circle:${matchValue}`;
export const iconLayerId = (matchValue) =>
    `hybrid-activity-points-icon:${matchValue}`;
export const clusterLayerId = (matchValue) =>
    `hybrid-activity-clusters:${matchValue}`;
export const clusterCountLayerId = (matchValue) =>
    `hybrid-activity-cluster-count:${matchValue}`;

// The circle layers the cards bind their hover and click to, and the cluster
// layers whose clicks expand or open a carousel.
export const circleLayerIds = (config) =>
    config.layers.map((layer) => circleLayerId(layer.matchValue));
export const clusterLayerIds = (config) =>
    config.layers.map((layer) => clusterLayerId(layer.matchValue));

// A marker is a white disc with the layer's colour on the border and the glyph:
// 24px across in all, radius plus the stroke Mapbox draws outside it.
const POINT_RADIUS = 11;
const POINT_BORDER_WIDTH = 1;

// Cluster tiles are only computed down to the source's maxzoom — past it the
// map reuses that zoom's tile — so a cluster still whole here never splits on
// screen, whatever expansion zoom the cluster index promises: the index runs
// on to clusterMaxZoom below. The click handler compares against this cap to
// know when to stop flying in and open the carousel instead. Mapbox's GeoJSON
// default, stated so the handler and the source cannot drift apart.
export const CLUSTER_SPLIT_MAX_ZOOM = 18;

// Clustered the way holdings are (utils/map/layers.js), but tighter: two
// incidents group once their centres come within CLUSTER_RADIUS, which for
// 24px markers is a visible overlap of a few pixels. Anything further apart
// draws as its own marker, so count bubbles only appear where markers would
// pile up and zooming pulls them apart early. Tuned by eye with the client:
// a radius past the marker width (30) clustered too eagerly, one of a single
// marker radius (11) not enough — this sits between. Holdings keep their own
// wider radius in their own sources. The cluster max zoom sits above any the tiles
// reach, so points still within the radius at CLUSTER_SPLIT_MAX_ZOOM — the
// same coordinates, or metres apart — remain a cluster at every zoom and
// answer with a card carousel instead.
//
// It cannot go higher than 30, though: supercluster packs a cluster's zoom
// plus one into the low five bits of its id, so a cluster made at zoom 31 or
// beyond decodes to the wrong zoom and getClusterExpansionZoom, walking a
// same-coordinate cluster's children up through every zoom, fails with "No
// cluster with the specified id" — and the click does nothing at all. 24 is
// the highest zoom Mapbox itself supports, so nothing is lost by stopping
// there.
const CLUSTER_RADIUS = 20;

const CLUSTER_OPTIONS = {
    cluster: true,
    clusterMaxZoom: 24,
    clusterRadius: CLUSTER_RADIUS,
    maxzoom: CLUSTER_SPLIT_MAX_ZOOM,
};

const CLUSTERED = ['has', 'point_count'];
const UNCLUSTERED = ['!', ['has', 'point_count']];

const circlePaint = (layer) => ({
    'circle-color': '#ffffff',
    'circle-stroke-color': layer.colour,
    'circle-stroke-width': POINT_BORDER_WIDTH,
    'circle-radius': POINT_RADIUS,
});

// The holdings cluster bubble, wearing the layer's colour so a reader can tell
// whose cluster it is at a glance.
const clusterPaint = (layer) => ({
    'circle-color': '#ffffff',
    'circle-stroke-color': layer.colour,
    'circle-stroke-width': 1.5,
    'circle-radius': ['step', ['get', 'point_count'], 18, 100, 21],
});

const clusterCountLayout = {
    'text-field': '{point_count_abbreviated}',
    'text-font': ['Source Sans Pro SemiBold', 'DIN Offc Pro Medium'],
    'text-size': 14,
    'text-allow-overlap': true,
    'text-ignore-placement': true,
};

/**
 * Draw a layer's glyphs, adding the symbol layer if this is the first render
 * that has an image to name — an icon that only loads on the second pass still
 * gets one. A layer whose icon failed keeps its bordered discs and loses only
 * the glyph, so '' — no image — has to be a valid layout value here.
 */
const drawGlyph = (map, layer, availableIconIds) => {
    const wanted = layer.icon ? iconImageId(layer.icon, layer.colour) : null;
    const image = wanted && availableIconIds.includes(wanted) ? wanted : '';

    if (map.getLayer(iconLayerId(layer.matchValue))) {
        map.setLayoutProperty(
            iconLayerId(layer.matchValue),
            'icon-image',
            image
        );
        return;
    }

    if (!image) {
        return;
    }

    map.addLayer({
        id: iconLayerId(layer.matchValue),
        type: 'symbol',
        source: pointsSourceId(layer.matchValue),
        filter: UNCLUSTERED,
        layout: {
            'icon-image': image,
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
        },
    });
};

const drawLayer = (map, layer, data, availableIconIds) => {
    const source = map.getSource(pointsSourceId(layer.matchValue));

    if (source) {
        source.setData(data);
        // The configuration can change under a source that is already there —
        // a colour edited in preview — so re-state what it drives rather than
        // leaving the first render's paint in place.
        map.setPaintProperty(
            circleLayerId(layer.matchValue),
            'circle-stroke-color',
            layer.colour
        );
        map.setPaintProperty(
            clusterLayerId(layer.matchValue),
            'circle-stroke-color',
            layer.colour
        );
        map.setPaintProperty(
            clusterCountLayerId(layer.matchValue),
            'text-color',
            layer.colour
        );
        drawGlyph(map, layer, availableIconIds);
        return;
    }

    map.addSource(pointsSourceId(layer.matchValue), {
        type: 'geojson',
        data,
        ...CLUSTER_OPTIONS,
    });

    map.addLayer({
        id: clusterLayerId(layer.matchValue),
        type: 'circle',
        source: pointsSourceId(layer.matchValue),
        filter: CLUSTERED,
        paint: clusterPaint(layer),
    });

    map.addLayer({
        id: clusterCountLayerId(layer.matchValue),
        type: 'symbol',
        source: pointsSourceId(layer.matchValue),
        filter: CLUSTERED,
        layout: clusterCountLayout,
        paint: { 'text-color': layer.colour },
    });

    map.addLayer({
        id: circleLayerId(layer.matchValue),
        type: 'circle',
        source: pointsSourceId(layer.matchValue),
        filter: UNCLUSTERED,
        paint: circlePaint(layer),
    });

    drawGlyph(map, layer, availableIconIds);
};

const removeLayerRendering = (map, matchValue) => {
    [
        clusterLayerId(matchValue),
        clusterCountLayerId(matchValue),
        circleLayerId(matchValue),
        iconLayerId(matchValue),
    ].forEach((id) => {
        if (map.getLayer(id)) {
            map.removeLayer(id);
        }
    });

    if (map.getSource(pointsSourceId(matchValue))) {
        map.removeSource(pointsSourceId(matchValue));
    }
};

// What each map currently draws, so a layer edited away in preview takes its
// source and layers with it rather than lingering with stale data.
const drawnValues = new WeakMap();

const draw = async (map, points, config) => {
    // Awaited first: a symbol layer is added in the same breath as the image
    // it names, so it never spends a frame asking for one that is not there yet.
    const availableIconIds = await loadLayerIcons(map, config.layers);

    const byValue = new Map();
    (points?.features || []).forEach((feature) => {
        const value = feature.properties?.[config.column];
        const grouped = byValue.get(value) || [];
        grouped.push(feature);
        byValue.set(value, grouped);
    });

    config.layers.forEach((layer) => {
        // A layer whose points are all filtered away — toggled off, or scrubbed
        // out of the date window — keeps its source with an empty collection,
        // so switching it back on is a setData rather than a rebuild.
        drawLayer(
            map,
            layer,
            {
                type: 'FeatureCollection',
                features: byValue.get(layer.matchValue) || [],
            },
            availableIconIds
        );
    });

    const configured = new Set(config.layers.map((layer) => layer.matchValue));
    (drawnValues.get(map) || []).forEach((value) => {
        if (!configured.has(value)) {
            removeLayerRendering(map, value);
        }
    });
    drawnValues.set(map, configured);
};

// Renders are queued per map because `draw` awaits the icons before it touches
// the sources: two overlapping calls would otherwise both find no source and
// both add it, which Mapbox rejects as a duplicate id.
const queues = new WeakMap();

/** Put the points on the map, or update them if they are already there. */
export const renderHybridActivityPoints = (map, points, config) => {
    const queued = Promise.resolve(queues.get(map)).then(() =>
        draw(map, points, config)
    );

    // Swallowed on the queue alone, so a render that throws still leaves the
    // next one able to run; the caller's promise keeps the rejection.
    queues.set(
        map,
        queued.catch(() => {})
    );

    return queued;
};

const STYLE_POLL_INTERVAL = 100;
const STYLE_POLL_TIMEOUT = 20000;

// Waiting on `map.loaded()` as well as the style means the shared map has
// finished adding its own layers, so ours goes on top of them.
const canTakeLayers = (map) =>
    map.loaded() && map.isStyleLoaded() && Boolean(map.getStyle());

/**
 * Run `render` once the style can take layers, now or when it is ready.
 *
 * The shared map hands its instance over before the style has loaded, and
 * addSource on a half-loaded style throws. This polls rather than waiting on
 * `styledata`, as the flowmap does: a style can settle without emitting another
 * event, and a listener attached just before that leaves the points on a map
 * that never renders them. Returns a teardown for the wait.
 */
export const whenStyleReady = (map, render) => {
    if (canTakeLayers(map)) {
        render();
        return () => {};
    }

    let timer = null;
    let waited = 0;

    const poll = () => {
        if (canTakeLayers(map)) {
            render();
            return;
        }

        waited += STYLE_POLL_INTERVAL;

        if (waited >= STYLE_POLL_TIMEOUT) {
            console.error(
                'Hybrid activity points: the map style never became ready.'
            );
            return;
        }

        timer = setTimeout(poll, STYLE_POLL_INTERVAL);
    };

    timer = setTimeout(poll, STYLE_POLL_INTERVAL);

    return () => clearTimeout(timer);
};
